import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import { useMe } from '@/hooks/use-me';
import { ConfettiProvider } from '@/providers/confetti';
import { SidebarProvider, useSidebar } from '@/providers/sidebar-context';
import { cls } from '@/utils/cls';

import { CurrentAssetProvider } from '../current-asset-provider';
import { ErrorBoundary } from '../error-boundary';
import { ErrorFallback } from '../ui/error-fallback';
import { UserPreferencesRedirect } from '../user-preferences-redirect';

import { AppHeader } from './app-header';
import { AppSidebarGlobal, AppSidebarLocal } from './app-sidebar';
import { DeprecationBanner } from './deprecation-banner';

export type ShellProps = {
  children: ReactNode;
};

const DEMO_BESS_CONFIG_ID = '49';

function ShellContent({ children }: ShellProps) {
  const { siteId } = useParams<{ siteId?: string }>();
  const { isOpen } = useSidebar();
  const me = useMe();

  const showDeprecationBanner = me.config.id === DEMO_BESS_CONFIG_ID;

  return (
    <div
      className={cls(
        'grid h-screen w-screen overflow-hidden',
        showDeprecationBanner
          ? 'grid-rows-[min-content_min-content_1fr]'
          : 'grid-rows-[min-content_1fr]',
      )}
    >
      <AppHeader className="border-zinc-950/5 bg-zinc-50" />
      {showDeprecationBanner && <DeprecationBanner />}
      <div className="flex size-full flex-row overflow-hidden">
        <div
          data-testid="layout-sidebar"
          className={cls(
            'shrink-0 overflow-hidden transition-all duration-300',
            isOpen ? 'w-48' : 'w-0',
          )}
        >
          <div className="h-full w-48">
            {siteId ? (
              <AppSidebarLocal
                className="border-zinc-950/5 bg-zinc-50 dark:bg-zinc-800"
                siteId={siteId}
              />
            ) : (
              <AppSidebarGlobal className="border-zinc-950/5 bg-zinc-50 dark:bg-zinc-800" />
            )}
          </div>
        </div>
        <main className="grow-1 relative size-full overflow-auto">
          <ErrorBoundary fallback={ErrorFallback}>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export function Shell({ children }: ShellProps) {
  return (
    <SidebarProvider>
      <CurrentAssetProvider>
        <ConfettiProvider>
          <UserPreferencesRedirect />
          <ShellContent>{children}</ShellContent>
        </ConfettiProvider>
      </CurrentAssetProvider>
    </SidebarProvider>
  );
}
